import { useState, useEffect, useMemo, useCallback, Fragment, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Search, Download, RefreshCw, ChevronLeft, ChevronRight,
    CheckCircle2, XCircle, Clock, Loader2, Eye, ChevronDown,
    ArrowUpDown, FileSpreadsheet, FileText, UploadCloud,
    Globe, Database, CheckSquare, Square, MinusSquare, Trash2, AlertTriangle, Layers, ScrollText, X,
    RefreshCcw, Tag, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import { useIndexedDB } from '../hooks/useIndexedDB';
import { apiClient, setApiTraceRecorder } from '../lib/api';
import { getSpecExtractModel, getExtractionEngine, getGeminiExtractionModel, getConditionGenerationModel, getPipelineStepEnabled } from '../lib/aiSettingsUtils';

import { exportToCSV, exportToExcelXLSX, parseImportedExcel, buildFlatRecord, buildBuExportLayout } from '../lib/excelExport';
import { injectManufacturerMeta } from '../lib/enrichmentUtils';
import { combineDescriptions, getDescriptions } from '../lib/descriptions';
import { useAuth } from '../lib/AuthContext';
import BatchStatusModal from '../components/BatchStatusModal';
import ProcessingLogModal from '../components/ProcessingLogModal';
import { removePartNumberAndNounModifier } from '../lib/extractedDataCleaner';
import { describeConditionMemory } from '../lib/conditionLog';
import OutputDataModal from '../components/OutputDataModal';
import ScrapCart from '../components/ScrapCart';

// ── Status config ────────────────────────────────────────────────────
const REVIEW_STATUS = {
    PENDING: { label: 'Pending', color: 'amber', icon: Clock },
    NM_PENDING: { label: 'N/M Pending', color: 'rose', icon: Tag },
    BATCH_PENDING: { label: 'Batch Pending', color: 'purple', icon: Loader2 },
    REVIEW: { label: 'Review', color: 'sky', icon: Eye },
    HOLD: { label: 'Hold', color: 'orange', icon: AlertTriangle },
    COMPLETED: { label: 'Completed', color: 'emerald', icon: CheckCircle2 },
    NO_MATCH: { label: 'No Match Found', color: 'red', icon: AlertTriangle },
};

const STATUS_BADGE_COLORS = {
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    rose: 'bg-rose-50 text-rose-700 ring-rose-200',
    purple: 'bg-purple-50 text-purple-700 ring-purple-200',
    sky: 'bg-sky-50 text-sky-700 ring-sky-200',
    orange: 'bg-orange-50 text-orange-700 ring-orange-200',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
};

const SOURCE_BADGE = {
    DB: 'bg-violet-50 text-violet-700 ring-violet-200',
    Online: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
};

const ITEMS_PER_PAGE = 25;

// ── Helper: truncate long text ───────────────────────────────────────
const TruncatedCell = ({ text, maxLen = 60 }) => {
    if (!text) return <span className="text-slate-400">—</span>;
    const str = String(text);
    if (str.length <= maxLen) return <span>{str}</span>;
    return <span title={str}>{str.slice(0, maxLen)}…</span>;
};

// ── Main component ───────────────────────────────────────────────────
function ResultsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { getAllItems, updateItem, addItem, deleteItem, loading: dbLoading } = useIndexedDB();
    const { isSuperAdmin } = useAuth();
    // Processing logs expose prompts and raw AI responses — super admins only
    const canViewLogs = isSuperAdmin;
    const importFileRef = useRef(null);

    // ── State ────────────────────────────────────────────────────
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [sourceFilter, setSourceFilter] = useState('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [expandedRow, setExpandedRow] = useState(null);
    const [savingIds, setSavingIds] = useState(new Set());
    const [confirmItem, setConfirmItem] = useState(null);
    const [outputFilter, setOutputFilter] = useState('ALL'); // 'ALL' | 'NO_OUTPUT' | 'HAS_OUTPUT'

    // ── Duplicate comparison modal state ──────────────────────────
    const [duplicateModal, setDuplicateModal] = useState(null); // { item, existingData, newData, matchedColumn, payload }

    // ── Bulk selection state ─────────────────────────────────────
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkSaving, setBulkSaving] = useState(false);

    // Delete popup state
    const [deletePopupOpen, setDeletePopupOpen] = useState(false);
    const [deleteFromTemp, setDeleteFromTemp] = useState(false);
    const [deleteFromDB, setDeleteFromDB] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // ── Batch status state ─────────────────────────────────────
    const [batchModalOpen, setBatchModalOpen] = useState(false);
    const [batchStatusData, setBatchStatusData] = useState(null);
    const [batchStatusLoading, setBatchStatusLoading] = useState(false);
    const [batchResultLoading, setBatchResultLoading] = useState(false);

    // ── Processing Log sidebar state ──────────────────────────
    const [logSidebarItem, setLogSidebarItem] = useState(null);
    const closeLogModal = useCallback(() => setLogSidebarItem(null), []);

    // ── Output data modal state ──────────────────────────────
    const [outputModalIndex, setOutputModalIndex] = useState(null);

    // ── Scrap Cart state (persisted to localStorage) ────────────
    const [cartItems, setCartItems] = useState(() => {
        try {
            const saved = localStorage.getItem('scrapCart');
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    });
    useEffect(() => {
        localStorage.setItem('scrapCart', JSON.stringify(cartItems));
    }, [cartItems]);

    // ── Hold modal state ─────────────────────────────────────────
    const [holdModalOpen, setHoldModalOpen] = useState(false);
    const [holdItem, setHoldItem] = useState(null);
    const [holdPrompt, setHoldPrompt] = useState('');
    const [holdLoading, setHoldLoading] = useState(false);
    const [holdSubmitting, setHoldSubmitting] = useState(false);

    // Get batch job name from navigation state or localStorage
    const batchJobName = location.state?.batchJobName || localStorage.getItem('batchJobName') || null;

    // ── Load data ────────────────────────────────────────────────
    const loadItems = useCallback(async () => {
        if (dbLoading) return;
        setLoading(true);
        try {
            const all = await getAllItems();
            // Normalize: fill missing nounModifier from chatgptOutput if available
            const normalized = (all || []).map(item => {
                if (!item.nounModifier && item.chatgptOutput) {
                    try {
                        let parsed = item.chatgptOutput;
                        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
                        const nm = parsed?.['NOUN/MODIFIER'] || parsed?.['NOUN_MODIFIER'] || '';
                        if (nm) return { ...item, nounModifier: nm };
                    } catch { /* ignore parse errors */ }
                }
                return item;
            });
            setItems(normalized);
        } catch (err) {
            console.error('Error loading items:', err);
            toast.error('Failed to load results');
        } finally {
            setLoading(false);
        }
    }, [dbLoading, getAllItems]);

    useEffect(() => { loadItems(); }, [loadItems]);
    useEffect(() => {
        if (location.state?.reload) loadItems();
    }, [location.state?.reload, loadItems]);

    // ── Counts ───────────────────────────────────────────────────
    const counts = useMemo(() => {
        const c = { PENDING: 0, NM_PENDING: 0, BATCH_PENDING: 0, REVIEW: 0, HOLD: 0, COMPLETED: 0, NO_MATCH: 0, NO_OUTPUT: 0, total: items.length };
        items.forEach(item => {
            const s = item.reviewStatus || 'PENDING';
            if (c[s] !== undefined) c[s]++;
            if (!item.chatgptOutput) c.NO_OUTPUT++;
        });
        return c;
    }, [items]);

    // ── Filtering + Sorting ──────────────────────────────────────
    const filteredItems = useMemo(() => {
        let result = items;

        if (statusFilter !== 'ALL') {
            result = result.filter(i => (i.reviewStatus || 'PENDING') === statusFilter);
        }
        if (sourceFilter !== 'ALL') {
            result = result.filter(i => (i.sourceType || 'Online') === sourceFilter);
        }
        if (outputFilter === 'NO_OUTPUT') {
            result = result.filter(i => !i.chatgptOutput);
        } else if (outputFilter === 'HAS_OUTPUT') {
            result = result.filter(i => !!i.chatgptOutput);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter(i =>
                combineDescriptions(i).toLowerCase().includes(q) ||
                (i.partNumber || '').toLowerCase().includes(q) ||
                (i.givenSupplierName || '').toLowerCase().includes(q) ||
                (i.givenSupplierPartnumber || '').toLowerCase().includes(q) ||
                (i.nounModifier || '').toLowerCase().includes(q) ||
                (i.manufacturerName || '').toLowerCase().includes(q) ||
                (i.manufacturerNumber || '').toLowerCase().includes(q)
            );
        }
        if (sortField) {
            result = [...result].sort((a, b) => {
                const av = a[sortField] || '';
                const bv = b[sortField] || '';
                const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
                return sortDir === 'asc' ? cmp : -cmp;
            });
        }
        return result;
    }, [items, statusFilter, sourceFilter, outputFilter, searchQuery, sortField, sortDir]);

    // ── Pagination ───────────────────────────────────────────────
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
    const pageItems = filteredItems.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );
    useEffect(() => { setCurrentPage(1); }, [statusFilter, sourceFilter, outputFilter, searchQuery]);

    // Clear selection when filters/search change
    useEffect(() => { setSelectedIds(new Set()); }, [statusFilter, sourceFilter, outputFilter, searchQuery]);

    // ── Sort handler ─────────────────────────────────────────────
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    // ── Status change handler ────────────────────────────────────
    // Helper: extract MFR PN 1 from chatgptOutput JSON (or fall back to input-level fields)
    const getMfrPN1 = (item) => {
        try {
            const parsed = typeof item.chatgptOutput === 'string' ? JSON.parse(item.chatgptOutput) : item.chatgptOutput;
            const fromOutput = parsed?.['MANUFACTURER PART NUMBER 1'] || parsed?.manufacturerpartnumber1 || null;
            if (fromOutput) return fromOutput;
        } catch { /* ignore */ }
        // Fallback: check input-level fields stored in IndexedDB
        return item.manufacturerNumber || item.manufacturerPartNumber || null;
    };

    const handleStatusChange = async (item, newStatus) => {
        const oldStatus = item.reviewStatus || 'PENDING';
        if (newStatus === oldStatus) return;

        // If changing to COMPLETED, show confirmation popup
        if (newStatus === 'COMPLETED') {
            setConfirmItem(item);
        } else {
            // Simple status update (Pending ↔ Review)
            try {
                await updateItem(item.id, { reviewStatus: newStatus });
                toast.success(`Status changed to ${REVIEW_STATUS[newStatus].label}`);
                loadItems();
            } catch (err) {
                toast.error('Failed to update status');
            }
        }
    };

    // ── Confirm save to DB (called from popup) ───────────────────
    const confirmSaveToDB = async () => {
        const item = confirmItem;
        if (!item) return;
        setConfirmItem(null);

        setSavingIds(prev => new Set(prev).add(item.id));

        try {
            // Inject manufacturer/supplier metadata into chatgptOutput before building record
            let enrichedItem = { ...item };
            try {
                const parsed = typeof item.chatgptOutput === 'string' ? JSON.parse(item.chatgptOutput) : item.chatgptOutput;
                const enriched = injectManufacturerMeta(parsed, item);
                enrichedItem.chatgptOutput = JSON.stringify(enriched);
                await updateItem(item.id, { chatgptOutput: enrichedItem.chatgptOutput });
            } catch { /* keep original if parse fails */ }

            // Build flat record using the SAME logic as Excel export
            // This guarantees DB data matches the exported Excel exactly
            const flatRecord = buildFlatRecord(enrichedItem);

            const result = await apiClient.saveMajorRecord({ record: flatRecord });

            if (result.action === 'duplicate') {
                // Duplicate found — show comparison modal
                setDuplicateModal({
                    item,
                    existingData: result.existingData,
                    newData: result.newData,
                    matchedColumn: result.matchedColumn,
                    existingId: result.existingId,
                    flatRecord, // Store for overwrite
                });
                setSavingIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
                return;
            }

            if (result.success || result.action) {
                await updateItem(item.id, { reviewStatus: 'COMPLETED' });
                toast.success(
                    `Saved to DB (${result.action || 'upserted'}): ${getMfrPN1(item)}`
                );
                loadItems();
            }
        } catch (err) {
            toast.error(err.message || 'Failed to save to database');
        } finally {
            setSavingIds(prev => {
                const next = new Set(prev);
                next.delete(item.id);
                return next;
            });
        }
    };

    // ── Force overwrite after duplicate comparison ───────────────
    const forceOverwriteSave = async () => {
        if (!duplicateModal) return;
        const { item, flatRecord, existingId } = duplicateModal;
        setDuplicateModal(null);
        setSavingIds(prev => new Set(prev).add(item.id));

        try {
            const result = await apiClient.saveMajorRecord({
                record: flatRecord,
                forceOverwrite: true,
                existingId,
            });
            if (result.success || result.action) {
                await updateItem(item.id, { reviewStatus: 'COMPLETED' });
                toast.success(`Overwritten in DB (${result.action}): ${getMfrPN1(item)}`);
                loadItems();
            }
        } catch (err) {
            toast.error(err.message || 'Failed to overwrite in database');
        } finally {
            setSavingIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
        }
    };

    // ── Keep existing (dismiss duplicate modal, mark as completed) ──
    const keepExistingRecord = async () => {
        if (!duplicateModal) return;
        const { item } = duplicateModal;
        setDuplicateModal(null);
        await updateItem(item.id, { reviewStatus: 'COMPLETED' });
        toast.info('Kept existing DB record unchanged.');
        loadItems();
    };

    // ── Hold prompt handlers ─────────────────────────────────────
    const fetchActiveBUPartAValues = async () => {
        try {
            const data = await apiClient.getActiveBUPartAValues();
            if (data.success && data.data?.valuesA) {
                return data.data.valuesA.map(v => v.value_text).filter(Boolean);
            }
        } catch (error) {
            console.error('Error fetching active BU type:', error);
        }
        return [];
    };

    const getDynamicAttributes = async (item) => {
        // 1. Use saved properties if available
        if (item.promptProperties?.length > 0) return item.promptProperties;

        // 2. Fallback: Fetch from active BU type (Part A values = property names)
        const buProperties = await fetchActiveBUPartAValues();
        if (buProperties.length > 0) return buProperties;

        // 3. Fallback: Extract property keys from existing mapped/AI output
        if (item.chatgptOutput) {
            try {
                const parsed = typeof item.chatgptOutput === 'string'
                    ? JSON.parse(item.chatgptOutput) : item.chatgptOutput;
                if (parsed && typeof parsed === 'object') {
                    const keys = Object.keys(parsed).filter(k => k && k !== '__meta');
                    if (keys.length > 0) return keys;
                }
            } catch (_) { /* not valid JSON */ }
        }

        // 4. Fallback: Extract from mappedProperties
        if (item.mappedProperties && typeof item.mappedProperties === 'object') {
            const keys = Object.keys(item.mappedProperties).filter(Boolean);
            if (keys.length > 0) return keys;
        }

        // No properties found
        return [];
    };

    const openHoldModal = async (item) => {
        setHoldItem(item);
        setHoldModalOpen(true);
        setHoldLoading(false);

        // Show the scraped content (Step 3 data) — NOT the Step 6 prompt
        setHoldPrompt(item.scrapedContent || '');
    };

    const submitHoldPrompt = async () => {
        if (!holdItem) return;
        setHoldSubmitting(true);
        let success = false;
        let errorMsg = '';
        let usedProvider = 'unknown';
        const resumeStartedAt = performance.now();
        const resumeLog = Array.isArray(holdItem.processingLog) ? [...holdItem.processingLog] : [];
        const logResume = (stage, action, result = 'info', data = {}) => {
            resumeLog.push({ stage, action, result, data, timestamp: new Date().toISOString() });
        };
        // Record every API call of this resume in the item's processing log
        setApiTraceRecorder(entry => resumeLog.push(entry));
        try {
            const editedScrapedContent = holdPrompt;
            const attrs = await getDynamicAttributes(holdItem);
            const desc = (holdItem.description || holdItem.itemDescription || '').trim();
            const nounMod = (holdItem.nounModifier || '').trim();

            logResume('INIT', 'User approved held content; resumed pipeline from Step 4', 'success', {
                contentLength: editedScrapedContent.length,
                nounModifier: nounMod,
            });
            await updateItem(holdItem.id, {
                reviewStatus: 'PROCESSING', status: 'processing', pipelineStep: 'clean_extract',
                scrapedContent: editedScrapedContent, processingLog: resumeLog,
            });

            console.log('[Hold] Processing:', { nounMod, attrsCount: attrs?.length, contentLen: editedScrapedContent?.length });

            if (!attrs || attrs.length === 0) {
                errorMsg = `No properties found for "${nounMod || 'this item'}". Configure in BU Type or Noun/Modifier Master.`;
                logResume('SPEC_EXTRACT', errorMsg, 'error');
                return;
            }

            // Step 4: AI Spec Extraction
            let specContent = null;
            if (editedScrapedContent) {
                logResume('SPEC_EXTRACT', 'Starting AI specification extraction for resumed HOLD record', 'info', {
                    contentLength: editedScrapedContent.length, model: getSpecExtractModel(),
                });
                try {
                    const cleanExtResp = await apiClient.stepCleanExtract({ scrapedContent: editedScrapedContent, model: getSpecExtractModel() });
                    if (cleanExtResp.success) {
                        specContent = cleanExtResp.specContent;
                        logResume('SPEC_EXTRACT', `Extracted specifications (${cleanExtResp.specLength || specContent?.length || 0} chars)`, 'success', {
                            originalLength: cleanExtResp.originalLength,
                            cleanedLength: cleanExtResp.cleanedLength,
                            specLength: cleanExtResp.specLength || specContent?.length || 0,
                            model: getSpecExtractModel(),
                        });
                    } else {
                        logResume('SPEC_EXTRACT', 'Spec extraction returned no result; using approved raw content', 'warning', { error: cleanExtResp.error || '' });
                    }
                } catch (e) {
                    console.warn('[Hold] Step 4 failed, using raw:', e.message);
                    logResume('SPEC_EXTRACT', 'Spec extraction failed; using approved raw content', 'warning', { error: e.message });
                }
            }

            // Step 5: Prompt Merge
            console.log('[Hold] Step 5: Merging', attrs.length, 'properties');
            logResume('PROMPT', `Building extraction prompt for ${attrs.length} properties`, 'info', { propertyCount: attrs.length });
            const mergeResp = await apiClient.propertyPromptMerge({
                propertyNames: attrs,
                content: specContent || editedScrapedContent || '',
                description: desc,
                nounModifier: nounMod
            });

            if (!mergeResp.merged || !mergeResp.prompt) {
                logResume('PROMPT', 'Prompt generation failed because no property conditions matched', 'error', { properties: attrs });
                errorMsg = 'Prompt generation failed — no property prompts matched for: ' + attrs.join(', ');
                return;
            }
            logResume('PROMPT', 'Extraction prompt built successfully', 'success', {
                promptLength: mergeResp.promptLength || mergeResp.prompt.length,
                matchedProperties: mergeResp.matchedProperties || [],
                unmatchedProperties: mergeResp.unmatchedProperties || [],
            });

            // Step 6: AI Extract — routed by Settings toggle
            const engine = getExtractionEngine();
            console.log(`[Hold] Step 6: Extracting with engine="${engine}"...`);
            logResume('EXTRACT', `Sending resumed record to ${engine} for final attribute extraction`, 'info', {
                engine, attributeCount: attrs.length,
            });
            let extractResp;

            if (engine === 'flan-t5') {
                // ── Flan-T5 Custom Model ──
                try {
                    extractResp = await apiClient.stepModelExtract({
                        cleanedContent: specContent || editedScrapedContent,
                        attributeLines: attrs, promptOverride: mergeResp.prompt,
                        description: desc, nounModifier: nounMod,
                    });
                    if (extractResp.success) usedProvider = 'custom-model';
                } catch (modelErr) {
                    console.error('[Hold] Custom Model error:', modelErr.message);
                    extractResp = { success: false, error: 'Flan-T5 inference server not available. Start: python inference_server.py --port 5050' };
                }
            } else {
                // ── Gemini + Dynamic Few-Shot Memory ──
                try {
                    extractResp = await apiClient.stepExtractWithMemory({
                        cleanedContent: specContent || editedScrapedContent,
                        attributeLines: attrs,
                        description: desc, nounModifier: nounMod,
                        manufacturerName: holdItem.manufacturerName || holdItem.givenSupplierName || '',
                        provider: engine === 'openai-luna' ? 'openai' : 'gemini',
                        model: engine === 'openai-luna' ? 'gpt-5.6-luna' : getGeminiExtractionModel(),
                        conditionModel: getConditionGenerationModel(),
                    });
                    if (extractResp.success) {
                        usedProvider = engine === 'openai-luna' ? 'openai-luna' : 'gemini-fewshot';
                        const tierEmoji = { tier1_both: '🎯', tier2_partial: '⚡', tier2_nm_only: '📂', tier2_mfr_only: '🏭', tier3_default: '🔲' };
                        console.log(`[Hold] ✅ Gemini+Memory: ${extractResp.fewShotExamplesUsed || 0} examples (${tierEmoji[extractResp.matchTier] || '❓'} ${extractResp.matchTier})`);
                        // Step 6 — Missing Property Conditions: rules from the DB and rules the AI wrote
                        if (extractResp.conditionMemory) {
                            const conditionLog = describeConditionMemory(extractResp.conditionMemory, { conditionModel: getConditionGenerationModel() });
                            logResume('CONDITION_MEMORY', conditionLog.action, conditionLog.result, conditionLog.data);
                        }
                    }
                } catch (geminiErr) {
                    console.error('[Hold] Gemini+Memory error:', geminiErr.message);
                    extractResp = { success: false, error: 'Gemini API error: ' + geminiErr.message };
                }
            }

            if (extractResp.success) {
                // Step 6.5 (Custom Code): remove extracted values that exactly match the Part Number or Noun/Modifier
                let extractedData = extractResp.attributes;
                if (getPipelineStepEnabled('step6_5')) {
                    const cleanup = removePartNumberAndNounModifier(extractedData, {
                        partNumbers: [holdItem.partNumber, holdItem.manufacturerNumber, holdItem.givenSupplierPartnumber,
                            holdItem.supplierPartNumber2, holdItem.supplierPartNumber3, holdItem.supplierPartNumber4],
                        nounModifier: nounMod,
                    });
                    extractedData = cleanup.data;
                    logResume('CUSTOM_CLEAN', cleanup.removed.length > 0
                        ? `Removed ${cleanup.removed.length} value(s) matching the Part Number / Noun/Modifier`
                        : 'No Part Number / Noun/Modifier values in extracted data', 'success', {
                        partNumbers: cleanup.terms.partNumbers,
                        nounModifier: cleanup.terms.nounModifier,
                        removed: cleanup.removed,
                        extractedData,
                    });
                }
                const enrichedAttrs = injectManufacturerMeta(extractedData, holdItem);
                const extractedCount = extractResp.attributeCount || Object.keys(extractResp.attributes || {}).length;
                logResume('EXTRACT', `Extracted ${extractedCount} attributes`, 'success', {
                    provider: usedProvider,
                    model: extractResp.extractionModel || (engine === 'gemini-fewshot' ? getGeminiExtractionModel() : engine),
                    attributeCount: extractedCount,
                    fewShotExamplesUsed: extractResp.fewShotExamplesUsed || 0,
                    matchTier: extractResp.matchTier || '',
                    mappedAttributes: extractResp.mapped || {},
                    fullPrompt: extractResp.fullPrompt || mergeResp.prompt,
                });
                const elapsedSeconds = ((performance.now() - resumeStartedAt) / 1000).toFixed(1);
                await updateItem(holdItem.id, {
                    ...holdItem,
                    scrapedContent: editedScrapedContent,
                    chatgptOutput: JSON.stringify(enrichedAttrs, null, 2),
                    chatgptRaw: JSON.stringify(extractResp.mapped || {}),
                    chatgptPrompt: extractResp.fullPrompt || mergeResp.prompt,
                    chatgptSuccess: true,
                    reviewStatus: 'PENDING',
                    mappedProperties: extractResp.mapped,
                    promptProperties: attrs,
                    extractionProvider: usedProvider,
                    resumeFromHold: false,
                    status: 'completed',
                    pipelineStep: 'complete',
                    processingLog: resumeLog,
                });
                if (extractResp.conditionMemory?.drafts?.length) {
                    try {
                        await apiClient.savePropertyConditions({
                            conditions: extractResp.conditionMemory.drafts,
                            nounModifier: nounMod,
                            manufacturerName: holdItem.manufacturerName || holdItem.givenSupplierName || '',
                        });
                        logResume('CONDITION_MEMORY', `Saved ${extractResp.conditionMemory.drafts.length} generated conditions`, 'success', {
                            savedCount: extractResp.conditionMemory.drafts.length,
                        });
                    } catch (conditionError) {
                        logResume('CONDITION_MEMORY', 'Product data saved, but condition memory save failed', 'warning', { error: conditionError.message });
                    }
                }
                logResume('COMPLETE', `HOLD resume completed successfully in ${elapsedSeconds}s; ready for final review`, 'success', {
                    provider: usedProvider, elapsedSeconds,
                });
                await updateItem(holdItem.id, { processingLog: resumeLog });
                success = true;
                console.log(`[Hold] ✅ Done via ${usedProvider}`);
            } else {
                errorMsg = 'Extraction failed: ' + (extractResp.error || 'Unknown error');
                logResume('EXTRACT', errorMsg, 'error', { provider: usedProvider, error: extractResp.error || 'Unknown error' });
            }
        } catch (e) {
            console.error('[Hold] Error:', e);
            errorMsg = 'Processing error: ' + (e.message || 'Unknown error');
            logResume('COMPLETE', errorMsg, 'error', { error: e.message || 'Unknown error' });
        } finally {
            setApiTraceRecorder(null);
            if (!success && holdItem?.id && resumeLog.length) {
                try {
                    if (resumeLog[resumeLog.length - 1]?.stage !== 'COMPLETE') {
                        logResume('COMPLETE', errorMsg || 'HOLD resume stopped before completion', 'error');
                    }
                    await updateItem(holdItem.id, {
                        reviewStatus: 'HOLD', status: 'hold', pipelineStep: 'error', processingLog: resumeLog,
                    });
                } catch (logSaveError) {
                    console.error('[Hold] Failed to save resume error log:', logSaveError);
                }
            }
            // ── ALWAYS close modal & reset state ──
            setHoldSubmitting(false);
            setHoldModalOpen(false);
            setHoldItem(null);
            setHoldPrompt('');

            if (success) {
                const label = usedProvider === 'custom-model' ? '🤖 Flan-T5' : '✨ Gemini+Memory';
                toast.success(`✅ Extracted via ${label} and moved to Pending review.`);
                loadItems();
            } else if (errorMsg) {
                toast.error(errorMsg);
                loadItems();
            }
        }
    };





    // ── Bulk selection handlers ──────────────────────────────────
    const pageItemIds = useMemo(() => new Set(pageItems.map(i => i.id)), [pageItems]);
    const allPageSelected = pageItems.length > 0 && pageItems.every(i => selectedIds.has(i.id));
    const somePageSelected = pageItems.some(i => selectedIds.has(i.id));

    const toggleSelectAll = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allPageSelected) {
                // Deselect all on current page
                pageItemIds.forEach(id => next.delete(id));
            } else {
                // Select all on current page
                pageItemIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    const toggleSelectItem = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    // ── Bulk status change handler ───────────────────────────────
    const handleBulkStatusChange = async (newStatus) => {
        if (selectedIds.size === 0) return;

        const selectedItems = items.filter(i => selectedIds.has(i.id));

        // If changing to COMPLETED, check all items have AI output
        if (newStatus === 'COMPLETED') {
            const missingOutput = selectedItems.filter(i => !i.chatgptOutput);
            if (missingOutput.length > 0) {
                toast.error(`${missingOutput.length} item(s) have no AI output — cannot set to Completed`);
                return;
            }
        }

        setBulkSaving(true);
        let successCount = 0;
        let failCount = 0;
        let duplicateCount = 0;

        try {
            for (const item of selectedItems) {
                const oldStatus = item.reviewStatus || 'PENDING';
                if (oldStatus === newStatus) {
                    successCount++;
                    continue;
                }

                try {
                    if (newStatus === 'COMPLETED') {
                        // Save to DB first
                        setSavingIds(prev => new Set(prev).add(item.id));
                        // Inject manufacturer meta into chatgptOutput
                        let enrichedOutput = item.chatgptOutput;
                        try {
                            const parsed = typeof enrichedOutput === 'string' ? JSON.parse(enrichedOutput) : enrichedOutput;
                            enrichedOutput = JSON.stringify(injectManufacturerMeta(parsed, item));
                            await updateItem(item.id, { chatgptOutput: enrichedOutput });
                        } catch { /* keep original */ }
                        const payload = { chatgptOutput: enrichedOutput, nounModifier: item.nounModifier || '' };
                        const result = await apiClient.saveMajorRecord(payload);
                        if (result.action === 'duplicate') {
                            // Skip duplicates in bulk — user must resolve individually
                            duplicateCount++;
                        } else if (result.success || result.action) {
                            await updateItem(item.id, { reviewStatus: 'COMPLETED' });
                            successCount++;
                        } else {
                            failCount++;
                        }
                        setSavingIds(prev => {
                            const next = new Set(prev);
                            next.delete(item.id);
                            return next;
                        });
                    } else {
                        await updateItem(item.id, { reviewStatus: newStatus });
                        successCount++;
                    }
                } catch (err) {
                    failCount++;
                    console.error(`Failed to update item ${item.id}:`, err);
                    setSavingIds(prev => {
                        const next = new Set(prev);
                        next.delete(item.id);
                        return next;
                    });
                }
            }

            if (successCount > 0) {
                toast.success(`${successCount} item(s) changed to ${REVIEW_STATUS[newStatus].label}`);
            }
            if (duplicateCount > 0) {
                toast.warning(`${duplicateCount} item(s) skipped — already exist in DB. Complete them individually to compare.`);
            }
            if (failCount > 0) {
                toast.error(`${failCount} item(s) failed to update`);
            }

            setSelectedIds(new Set());
            loadItems();
        } finally {
            setBulkSaving(false);
        }
    };

    // ── Export handlers ──────────────────────────────────────────
    // Export columns follow the active Backend BU; fall back to the full layout without one
    const loadExportLayout = async () => {
        try {
            const data = await apiClient.getActiveBUPartAValues();
            const layout = data?.success ? buildBuExportLayout(data.data) : null;
            if (!layout) toast.info('No active Backend BU — exporting the full column layout');
            return layout;
        } catch (error) {
            console.error('Failed to load active Backend BU for export:', error);
            toast.warning('Could not load the active Backend BU — exporting the full column layout');
            return null;
        }
    };

    const runExport = async (exporter, formatLabel) => {
        if (filteredItems.length === 0) { toast.error('No items to export'); return; }
        const layout = await loadExportLayout();
        const { truncatedRows = 0 } = exporter(filteredItems, layout) || {};
        toast.success(`Exported ${filteredItems.length} items as ${formatLabel}${layout ? ` (${layout.name})` : ''}`);
        if (truncatedRows > 0) {
            toast.warning(`${truncatedRows} item(s) have more properties than "${layout.name}" has PROPERTY columns — the extra properties were left out`);
        }
    };

    const handleExportCSV = () => runExport(exportToCSV, 'CSV');
    const handleExportExcel = () => runExport(exportToExcelXLSX, 'Excel');

    // ── Import handler ──────────────────────────────────────────
    const handleImportExcel = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Reset input so the same file can be re-imported
        e.target.value = '';

        try {
            toast.info('Reading imported file…');
            const buffer = await file.arrayBuffer();
            const importedItems = parseImportedExcel(buffer);

            if (importedItems.length === 0) {
                toast.error('No valid rows found in imported file');
                return;
            }

            // Build lookups from current items: Part Number first, Supplier PN as fallback
            const currentItems = await getAllItems();
            const partNumberLookup = new Map();
            const supplierPnLookup = new Map();
            currentItems.forEach(item => {
                if (item.partNumber) {
                    partNumberLookup.set(String(item.partNumber).trim().toUpperCase(), item);
                }
                if (item.givenSupplierPartnumber) {
                    supplierPnLookup.set(String(item.givenSupplierPartnumber).trim(), item);
                }
            });

            let updated = 0;
            let added = 0;

            for (const importedItem of importedItems) {
                const existing = importedItem.partNumber
                    ? partNumberLookup.get(importedItem.partNumber.toUpperCase())
                    : supplierPnLookup.get(importedItem.givenSupplierPartnumber);
                if (existing) {
                    // Update existing item with imported data
                    await updateItem(existing.id, {
                        partNumber: importedItem.partNumber || existing.partNumber || '',
                        description1: importedItem.description1 || getDescriptions(existing).description1,
                        description2: importedItem.description2 || getDescriptions(existing).description2,
                        description: combineDescriptions(importedItem) || combineDescriptions(existing),
                        givenSupplierName: importedItem.givenSupplierName || existing.givenSupplierName,
                        nounModifier: importedItem.nounModifier || existing.nounModifier,
                        chatgptOutput: importedItem.chatgptOutput || existing.chatgptOutput,
                        reviewStatus: importedItem.reviewStatus || existing.reviewStatus,
                        sourceType: importedItem.sourceType || existing.sourceType,
                    });
                    updated++;
                } else {
                    // Add as new row
                    await addItem({
                        ...importedItem,
                        itemDescription: importedItem.description1,
                        status: 'completed',
                    });
                    added++;
                }
            }

            toast.success(`Imported: ${updated} updated, ${added} new rows added`);
            loadItems();
        } catch (err) {
            console.error('Import error:', err);
            toast.error(`Import failed: ${err.message}`);
        }
    };

    // ── Delete handler ───────────────────────────────────────────
    const openDeletePopup = () => {
        if (selectedIds.size === 0) { toast.error('No items selected'); return; }
        setDeleteFromTemp(false);
        setDeleteFromDB(false);
        setDeletePopupOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteFromTemp && !deleteFromDB) {
            toast.error('Select at least one delete option');
            return;
        }
        setDeleting(true);
        try {
            const selectedItems = items.filter(i => selectedIds.has(i.id));

            // Collect DB-identifiable part numbers from multiple sources
            const partNumberSet = new Set();
            for (const item of selectedItems) {
                const mfrPN1 = getMfrPN1(item);
                if (mfrPN1) partNumberSet.add(mfrPN1);
                if (item.manufacturerNumber) partNumberSet.add(String(item.manufacturerNumber).trim());
                if (item.givenSupplierPartnumber) partNumberSet.add(String(item.givenSupplierPartnumber).trim());
            }
            const partNumbers = [...partNumberSet].filter(Boolean);

            let tempDeleted = 0;
            let dbDeleted = 0;

            // Delete from Temp (IndexedDB)
            if (deleteFromTemp) {
                for (const item of selectedItems) {
                    await deleteItem(item.id);
                    tempDeleted++;
                }
            }

            // Delete from DB (Major_table)
            if (deleteFromDB && partNumbers.length > 0) {
                try {
                    const resp = await apiClient.deleteMajorRecords(partNumbers);
                    dbDeleted = resp?.deleted || 0;
                } catch (err) {
                    console.error('DB delete failed:', err);
                    toast.error(`DB delete failed: ${err.message}`);
                }
            }

            const msgs = [];
            if (deleteFromTemp) msgs.push(`${tempDeleted} from Temp`);
            if (deleteFromDB) msgs.push(`${dbDeleted} from DB`);
            toast.success(`Deleted: ${msgs.join(', ')}`);

            setSelectedIds(new Set());
            setDeletePopupOpen(false);
            loadItems();
        } catch (err) {
            console.error('Delete error:', err);
            toast.error(`Delete failed: ${err.message}`);
        } finally {
            setDeleting(false);
        }
    };

    // ── Batch Status Handlers ─────────────────────────────────────
    const handleCheckBatchStatus = useCallback(async () => {
        if (!batchJobName) {
            toast.error('No batch job found');
            return;
        }
        setBatchStatusLoading(true);
        try {
            const batchId = batchJobName.replace('batches/', '');
            const result = await apiClient.checkGeminiBatchStatus(batchId);
            setBatchStatusData(result);
        } catch (err) {
            toast.error(`Status check failed: ${err.message}`);
        } finally {
            setBatchStatusLoading(false);
        }
    }, [batchJobName]);

    const handleFetchBatchResults = async () => {
        if (!batchJobName) return;
        setBatchResultLoading(true);
        try {
            const batchId = batchJobName.replace('batches/', '');
            const result = await apiClient.fetchGeminiBatchResults(batchId);

            if (result.state !== 'JOB_STATE_SUCCEEDED') {
                toast.warning(result.error || 'Batch not ready yet');
                return;
            }

            if (!result.results || result.results.length === 0) {
                toast.warning('No results returned from batch');
                return;
            }

            // Merge batch results into IndexedDB items
            const currentItems = await getAllItems();
            let updatedCount = 0;

            for (const batchResult of result.results) {
                const rowNum = batchResult.rowNumber;
                const matchItem = currentItems.find(i => Number(i.rowNumber) === Number(rowNum));
                if (matchItem) {
                    // Inject manufacturer/supplier metadata from original input row
                    const enrichedOutput = batchResult.success
                        ? injectManufacturerMeta(batchResult.output, matchItem)
                        : null;
                    const updates = {
                        chatgptSuccess: batchResult.success,
                        chatgptOutput: enrichedOutput ? JSON.stringify(enrichedOutput) : null,
                        chatgptRaw: batchResult.raw || null,
                        chatgptModel: batchResult.model || 'gemini-3.1-flash-lite',
                        chatgptError: batchResult.error || null,
                        reviewStatus: batchResult.success ? 'PENDING' : 'REVIEW',
                    };
                    await updateItem(matchItem.id, updates);
                    updatedCount++;
                }
            }

            toast.success(`Fetched ${updatedCount} result(s) from batch`);
            localStorage.removeItem('batchJobName');
            localStorage.removeItem('batchPrompts');
            localStorage.removeItem('batchModel');
            setBatchModalOpen(false);
            loadItems();
        } catch (err) {
            toast.error(`Failed to fetch results: ${err.message}`);
        } finally {
            setBatchResultLoading(false);
        }
    };

    const handleRetryBatch = async () => {
        try {
            const storedPrompts = localStorage.getItem('batchPrompts');
            const storedModel = localStorage.getItem('batchModel');
            if (!storedPrompts) {
                toast.error('No stored prompts found for retry');
                return;
            }
            const prompts = JSON.parse(storedPrompts);
            const result = await apiClient.retryGeminiBatch({
                prompts,
                model: storedModel || 'gemini-3.1-flash-lite'
            });
            localStorage.setItem('batchJobName', result.batchJobName);
            setBatchStatusData(null);
            toast.success(`Retry submitted! New job: ${result.batchJobName}`);
        } catch (err) {
            toast.error(`Retry failed: ${err.message}`);
        }
    };

    // ── Status Badge ─────────────────────────────────────────────
    const StatusBadge = ({ status }) => {
        const cfg = REVIEW_STATUS[status] || REVIEW_STATUS.PENDING;
        const Icon = cfg.icon;
        const colors = STATUS_BADGE_COLORS[cfg.color] || STATUS_BADGE_COLORS.amber;
        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${colors}`}>
                <Icon className="w-3.5 h-3.5" />
                {cfg.label}
            </span>
        );
    };

    // ── Source Badge ──────────────────────────────────────────────
    const SourceBadge = ({ source }) => {
        const s = source || 'Online';
        const colors = SOURCE_BADGE[s] || SOURCE_BADGE.Online;
        const Icon = s === 'DB' ? Database : Globe;
        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${colors}`}>
                <Icon className="w-3.5 h-3.5" />
                {s}
            </span>
        );
    };

    // ── Loading state ────────────────────────────────────────────
    if (loading || dbLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center space-y-4">
                    <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto" />
                    <p className="text-slate-500 font-medium">Loading results…</p>
                </div>
            </div>
        );
    }

    // ── Table columns config ─────────────────────────────────────
    const columns = [
        { key: '_select', label: '', width: 'w-10', sortable: false },
        { key: '_cart', label: '', width: 'w-10', sortable: false },
        { key: 'partNumber', label: 'Part Number', width: 'w-36', sortable: true },
        { key: 'description1', label: 'Description 1', width: '', sortable: true },
        { key: 'description2', label: 'Description 2', width: '', sortable: true },
        { key: 'givenSupplierName', label: 'Supplier Name', width: 'w-40', sortable: true },
        { key: 'givenSupplierPartnumber', label: 'Supplier Part #', width: 'w-40', sortable: true },
        { key: 'nounModifier', label: 'Noun/Modifier', width: 'w-36', sortable: true },
        { key: 'chatgptOutput', label: 'AI Output', width: 'w-52', sortable: false },
        { key: 'sourceType', label: 'Source', width: 'w-28', sortable: true },
        { key: 'reviewStatus', label: 'Status', width: 'w-36', sortable: true },
        ...(canViewLogs ? [{ key: '_log', label: 'Log', width: 'w-16', sortable: false }] : []),
    ];

    // ── Sidebar cards ────────────────────────────────────────────
    const sidebarCards = [
        { key: 'PENDING', label: 'Pending', count: counts.PENDING, gradient: 'from-amber-500 to-amber-600', icon: Clock },
        { key: 'NM_PENDING', label: 'N/M Pending', count: counts.NM_PENDING, gradient: 'from-rose-500 to-rose-600', icon: Tag },
        { key: 'BATCH_PENDING', label: 'Batch Pending', count: counts.BATCH_PENDING, gradient: 'from-purple-500 to-purple-600', icon: Loader2 },
        { key: 'REVIEW', label: 'Review', count: counts.REVIEW, gradient: 'from-sky-500 to-sky-600', icon: Eye },
        { key: 'HOLD', label: 'Hold', count: counts.HOLD, gradient: 'from-orange-500 to-orange-600', icon: AlertTriangle },
        { key: 'COMPLETED', label: 'Completed', count: counts.COMPLETED, gradient: 'from-emerald-500 to-emerald-600', icon: CheckCircle2 },
        { key: 'NO_MATCH', label: 'No Match Found', count: counts.NO_MATCH, gradient: 'from-red-500 to-red-600', icon: AlertTriangle },
    ];

    return (
        <div className="flex flex-col lg:flex-row gap-0 h-[calc(100vh-80px)] animate-fade-in">

            {/* ── LEFT SIDEBAR ─────────────────────────────────── */}
            <aside className="lg:w-[240px] shrink-0 bg-white/90 backdrop-blur-lg border-r border-slate-200/60 p-4 flex flex-col gap-4 lg:overflow-y-auto">
                {/* Summary cards */}
                <div className="space-y-3">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Summary</p>
                    {sidebarCards.map(card => {
                        const Icon = card.icon;
                        const isActive = statusFilter === card.key;
                        return (
                            <button
                                key={card.key}
                                onClick={() => setStatusFilter(statusFilter === card.key ? 'ALL' : card.key)}
                                className={`w-full relative overflow-hidden rounded-xl p-3.5 text-left transition-all duration-200
                                    bg-gradient-to-br ${card.gradient} text-white shadow-md
                                    hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]
                                    ${isActive ? 'ring-2 ring-offset-2 ring-primary-400' : ''}`}
                            >
                                <div className="absolute top-2.5 right-2.5 bg-white/20 p-1.5 rounded-lg">
                                    <Icon className="w-4 h-4 text-white/80" />
                                </div>
                                <p className="text-2xl font-extrabold">{card.count}</p>
                                <p className="text-xs font-medium text-white/75 mt-0.5">{card.label}</p>
                            </button>
                        );
                    })}

                    {/* Total */}
                    <div className="rounded-xl p-3.5 bg-gradient-to-br from-slate-600 to-slate-800 text-white shadow-md">
                        <p className="text-2xl font-extrabold">{counts.total}</p>
                        <p className="text-xs font-medium text-white/75 mt-0.5">Total Items</p>
                    </div>

                    {/* Retry NM Pending — auto-rescrape all NM_PENDING items */}
                    {counts.NM_PENDING > 0 && (
                        <button
                            onClick={() => {
                                const nmPendingItems = items.filter(i => i.reviewStatus === 'NM_PENDING');
                                if (nmPendingItems.length === 0) {
                                    toast.warning('No NM Pending items found');
                                    return;
                                }
                                const rescrapeKey = `rescrape-nm-retry-${Date.now()}`;
                                sessionStorage.setItem(rescrapeKey, JSON.stringify(nmPendingItems));
                                navigate('/', { state: { rescrapeKey, rescrapeCount: nmPendingItems.length, autoStart: true } });
                            }}
                            className="w-full rounded-xl p-3 text-left transition-all duration-200
                                bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-md
                                hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                        >
                            <div className="flex items-center gap-2">
                                <RefreshCcw className="w-4 h-4" />
                                <div>
                                    <p className="text-sm font-bold">Retry NM Pending</p>
                                    <p className="text-[10px] text-white/70">Re-scrape {counts.NM_PENDING} item(s) from scratch</p>
                                </div>
                            </div>
                        </button>
                    )}

                    {/* No Output */}
                    <button
                        onClick={() => setOutputFilter(outputFilter === 'NO_OUTPUT' ? 'ALL' : 'NO_OUTPUT')}
                        className={`w-full relative overflow-hidden rounded-xl p-3.5 text-left transition-all duration-200
                            bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-md
                            hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]
                            ${outputFilter === 'NO_OUTPUT' ? 'ring-2 ring-offset-2 ring-red-400' : ''}`}
                    >
                        <div className="absolute top-2.5 right-2.5 bg-white/20 p-1.5 rounded-lg">
                            <XCircle className="w-4 h-4 text-white/80" />
                        </div>
                        <p className="text-2xl font-extrabold">{counts.NO_OUTPUT}</p>
                        <p className="text-xs font-medium text-white/75 mt-0.5">No Output</p>
                    </button>

                    {/* ── Source Data Link ── */}
                    <button
                        onClick={() => navigate('/source-data')}
                        className="w-full relative overflow-hidden rounded-xl p-3.5 text-left transition-all duration-200
                            bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-md
                            hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <div className="absolute top-2.5 right-2.5 bg-white/20 p-1.5 rounded-lg">
                            <Database className="w-4 h-4 text-white/80" />
                        </div>
                        <p className="text-sm font-bold">Source Data →</p>
                        <p className="text-xs font-medium text-white/70 mt-0.5">View saved records</p>
                    </button>
                </div>

                {/* All-completed banner */}
                {counts.total > 0 && counts.COMPLETED === counts.total && (
                    <div className="rounded-xl p-4 text-center space-y-2 mt-2"
                        style={{
                            background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(59,130,246,0.1))',
                            border: '1px solid rgba(16,185,129,0.25)',
                        }}>
                        <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
                        <p className="text-sm font-semibold text-emerald-700">All items completed!</p>
                        <button
                            onClick={() => navigate('/source-data')}
                            className="w-full py-2 rounded-lg text-sm font-bold text-white transition-all
                                bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-md"
                        >
                            View Source Data →
                        </button>
                    </div>
                )}

                {/* Actions */}
                <div className="space-y-2 mt-auto">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Actions</p>

                    {/* Batch Status Button */}
                    {batchJobName && (
                        <button
                            onClick={() => { setBatchModalOpen(true); handleCheckBatchStatus(); }}
                            className="w-full text-sm !py-2.5 justify-start inline-flex items-center gap-2
                                       px-4 rounded-xl font-semibold transition-all duration-200
                                       bg-gradient-to-r from-indigo-500 to-purple-500 text-white
                                       hover:from-indigo-600 hover:to-purple-600 shadow-md hover:shadow-lg"
                        >
                            <Layers className="w-4 h-4" />
                            Check Batch Status
                        </button>
                    )}

                    <button onClick={handleExportExcel} className="w-full btn-primary text-sm !py-2.5 justify-start">
                        <FileSpreadsheet className="w-4 h-4" />
                        Export Excel
                    </button>
                    <button onClick={handleExportCSV} className="w-full btn-secondary text-sm !py-2.5 justify-start">
                        <FileText className="w-4 h-4" />
                        Export CSV
                    </button>
                    <button
                        onClick={() => importFileRef.current?.click()}
                        className="w-full btn-secondary text-sm !py-2.5 justify-start"
                    >
                        <UploadCloud className="w-4 h-4" />
                        Import Excel
                    </button>
                    <input
                        ref={importFileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={handleImportExcel}
                    />
                </div>
            </aside>

            {/* ── RIGHT CONTENT AREA ──────────────────────────── */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

                {/* ── Top bar: search + filters ───────────────── */}
                <div className="shrink-0 bg-white/80 backdrop-blur-lg border-b border-slate-200/60 px-4 py-3">
                    <div className="flex items-center gap-3 overflow-x-auto scrollbar-thin">
                        {/* Search */}
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search description, supplier, part #, noun…"
                                className="w-full pl-10 pr-4 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl
                                           text-sm text-slate-800 placeholder:text-slate-400
                                           focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400
                                           transition-all duration-200"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    <XCircle className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Status filter */}
                        <div className="relative shrink-0">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="appearance-none pl-3 pr-8 py-2.5 bg-slate-50/80 border border-slate-200
                                           rounded-xl text-sm text-slate-700 font-medium cursor-pointer
                                           focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400
                                           transition-all duration-200"
                            >
                                <option value="ALL">All Statuses ({counts.total})</option>
                                {Object.entries(REVIEW_STATUS).map(([key, cfg]) => (
                                    <option key={key} value={key}>
                                        {cfg.label} ({counts[key] || 0})
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>

                        {/* Source filter */}
                        <div className="relative shrink-0">
                            <select
                                value={sourceFilter}
                                onChange={(e) => setSourceFilter(e.target.value)}
                                className="appearance-none pl-3 pr-8 py-2.5 bg-slate-50/80 border border-slate-200
                                           rounded-xl text-sm text-slate-700 font-medium cursor-pointer
                                           focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400
                                           transition-all duration-200"
                            >
                                <option value="ALL">All Sources</option>
                                <option value="DB">DB</option>
                                <option value="Online">Online</option>
                            </select>
                            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>

                        {/* Refresh */}
                        <button onClick={loadItems} className="shrink-0 btn-secondary text-sm !py-2.5 !px-3" title="Refresh">
                            <RefreshCw className="w-4 h-4" />
                        </button>

                        {/* Item count */}
                        <span className="shrink-0 text-xs text-slate-500 whitespace-nowrap">
                            {filteredItems.length} of {items.length} items
                        </span>
                    </div>

                    {/* Active filter pills */}
                    {(statusFilter !== 'ALL' || sourceFilter !== 'ALL' || searchQuery) && (
                        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                            {statusFilter !== 'ALL' && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 ring-1 ring-primary-200">
                                    Status: {REVIEW_STATUS[statusFilter]?.label}
                                    <button onClick={() => setStatusFilter('ALL')} className="hover:text-primary-900">
                                        <XCircle className="w-3.5 h-3.5" />
                                    </button>
                                </span>
                            )}
                            {sourceFilter !== 'ALL' && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 ring-1 ring-violet-200">
                                    Source: {sourceFilter}
                                    <button onClick={() => setSourceFilter('ALL')} className="hover:text-violet-900">
                                        <XCircle className="w-3.5 h-3.5" />
                                    </button>
                                </span>
                            )}
                            {searchQuery && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 ring-1 ring-primary-200">
                                    Search: &quot;{searchQuery}&quot;
                                    <button onClick={() => setSearchQuery('')} className="hover:text-primary-900">
                                        <XCircle className="w-3.5 h-3.5" />
                                    </button>
                                </span>
                            )}
                            <button
                                onClick={() => { setStatusFilter('ALL'); setSourceFilter('ALL'); setSearchQuery(''); }}
                                className="text-xs text-slate-500 hover:text-primary-600 font-medium transition-colors"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Bulk Action Bar (shows when items are selected) ── */}
                {selectedIds.size > 0 && (
                    <div className="shrink-0 bg-primary-50 border-b border-primary-200 px-4 py-2.5 flex items-center gap-3 animate-fade-in">
                        <div className="flex items-center gap-2">
                            <CheckSquare className="w-4 h-4 text-primary-600" />
                            <span className="text-sm font-semibold text-primary-700">
                                {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} selected
                            </span>
                        </div>

                        <div className="h-5 w-px bg-primary-200" />

                        <span className="text-xs font-medium text-primary-600">Change status to:</span>

                        {Object.entries(REVIEW_STATUS).map(([key, cfg]) => {
                            const Icon = cfg.icon;
                            const colorMap = {
                                PENDING: 'bg-amber-100 text-amber-700 hover:bg-amber-200 border-amber-300',
                                REVIEW: 'bg-sky-100 text-sky-700 hover:bg-sky-200 border-sky-300',
                                COMPLETED: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-300',
                            };
                            return (
                                <button
                                    key={key}
                                    onClick={() => handleBulkStatusChange(key)}
                                    disabled={bulkSaving}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                               border transition-all duration-200 disabled:opacity-50
                                               ${colorMap[key]}`}
                                >
                                    {bulkSaving ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <Icon className="w-3.5 h-3.5" />
                                    )}
                                    {cfg.label}
                                </button>
                            );
                        })}

                        <div className="ml-auto flex items-center gap-2">
                            <button
                                onClick={() => {
                                    const newIds = Array.from(selectedIds);
                                    setCartItems(prev => {
                                        const merged = new Set([...prev, ...newIds]);
                                        return Array.from(merged);
                                    });
                                    toast.success(`${newIds.length} item(s) added to Scrap Cart`);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                           bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-300
                                           transition-all duration-200"
                            >
                                <RefreshCcw className="w-3.5 h-3.5" />
                                Rescrape ({selectedIds.size})
                            </button>
                            <button
                                onClick={openDeletePopup}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                           bg-red-100 text-red-700 hover:bg-red-200 border border-red-300
                                           transition-all duration-200"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete
                            </button>
                            <button
                                onClick={clearSelection}
                                className="text-xs text-primary-600 hover:text-primary-800 font-medium transition-colors flex items-center gap-1"
                            >
                                <XCircle className="w-3.5 h-3.5" />
                                Clear selection
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Data Table ──────────────────────────────── */}
                <div className="flex-1 overflow-hidden flex flex-col bg-white/90 backdrop-blur-lg">
                    {filteredItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center flex-1">
                            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                                <Search className="w-7 h-7 text-slate-400" />
                            </div>
                            <p className="text-slate-600 font-semibold">No results found</p>
                            <p className="text-slate-400 text-sm mt-1">Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <>
                            {/* Scrollable table wrapper */}
                            <div className="flex-1 overflow-auto">
                                <table className="w-full text-sm min-w-[900px]">
                                    <thead className="sticky top-0 z-10">
                                        <tr className="border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
                                            {columns.map(col => (
                                                <th
                                                    key={col.key}
                                                    onClick={() => {
                                                        if (col.key === '_select') {
                                                            toggleSelectAll();
                                                        } else if (col.sortable) {
                                                            handleSort(col.key);
                                                        }
                                                    }}
                                                    className={`px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider
                                                                ${col.sortable || col.key === '_select' ? 'cursor-pointer hover:text-slate-700' : ''}
                                                                select-none transition-colors ${col.width}`}
                                                >
                                                    {col.key === '_select' ? (
                                                        <span className="inline-flex items-center text-primary-500">
                                                            {allPageSelected ? (
                                                                <CheckSquare className="w-4.5 h-4.5" />
                                                            ) : somePageSelected ? (
                                                                <MinusSquare className="w-4.5 h-4.5" />
                                                            ) : (
                                                                <Square className="w-4.5 h-4.5" />
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1">
                                                            {col.label}
                                                            {col.sortable && (
                                                                <ArrowUpDown
                                                                    className={`w-3.5 h-3.5 transition-colors
                                                                        ${sortField === col.key ? 'text-primary-500' : 'text-slate-300'}`}
                                                                />
                                                            )}
                                                        </span>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {pageItems.map((item) => {
                                            const isSaving = savingIds.has(item.id);
                                            const isExpanded = expandedRow === item.id;
                                            const currentStatus = item.reviewStatus || 'PENDING';
                                            const isSelected = selectedIds.has(item.id);
                                            return (
                                                <Fragment key={item.id}>
                                                    <tr className={`group hover:bg-primary-50/40 transition-colors
                                                        ${isExpanded ? 'bg-primary-50/30' : ''}
                                                        ${isSelected ? 'bg-primary-50/60' : ''}`}
                                                    >
                                                        {/* Checkbox */}
                                                        <td className="px-3 py-3">
                                                            <button
                                                                onClick={() => toggleSelectItem(item.id)}
                                                                className={`transition-colors ${isSelected ? 'text-primary-600' : 'text-slate-300 hover:text-slate-500'}`}
                                                            >
                                                                {isSelected ? (
                                                                    <CheckSquare className="w-4.5 h-4.5" />
                                                                ) : (
                                                                    <Square className="w-4.5 h-4.5" />
                                                                )}
                                                            </button>
                                                        </td>

                                                        {/* Cart */}
                                                        <td className="px-2 py-3">
                                                            <button
                                                                onClick={() => {
                                                                    setCartItems(prev =>
                                                                        prev.includes(item.id)
                                                                            ? prev.filter(cid => cid !== item.id)
                                                                            : [...prev, item.id]
                                                                    );
                                                                }}
                                                                className={`p-1.5 rounded-lg transition-all ${cartItems.includes(item.id)
                                                                    ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                                                                    : 'text-slate-300 hover:text-amber-500 hover:bg-amber-50/50'}`}
                                                                title={cartItems.includes(item.id) ? 'Remove from cart' : 'Add to cart'}
                                                            >
                                                                <ShoppingCart className="w-4 h-4" />
                                                            </button>
                                                        </td>

                                                        {/* Part Number */}
                                                        <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">
                                                            {item.partNumber
                                                                ? <span className="font-semibold text-slate-800">{item.partNumber}</span>
                                                                : <span className="text-slate-300">—</span>}
                                                        </td>

                                                        {/* Description 1 */}
                                                        <td className="px-3 py-3 text-slate-700 max-w-[300px]">
                                                            <TruncatedCell text={getDescriptions(item).description1} maxLen={80} />
                                                        </td>

                                                        {/* Description 2 */}
                                                        <td className="px-3 py-3 text-slate-700 max-w-[300px]">
                                                            <TruncatedCell text={getDescriptions(item).description2} maxLen={80} />
                                                        </td>

                                                        {/* Supplier Name */}
                                                        <td className="px-3 py-3 text-slate-700">
                                                            <TruncatedCell text={item.givenSupplierName} maxLen={30} />
                                                        </td>

                                                        {/* Supplier Part # */}
                                                        <td className="px-3 py-3 font-medium text-slate-800">
                                                            <TruncatedCell text={item.givenSupplierPartnumber} maxLen={30} />
                                                        </td>

                                                        {/* Noun/Modifier */}
                                                        <td className="px-3 py-3 text-slate-600">
                                                            <TruncatedCell text={item.nounModifier} maxLen={30} />
                                                        </td>

                                                        {/* AI Output */}
                                                        <td className="px-3 py-3">
                                                            {item.reviewStatus === 'HOLD' && item.scrapedContent ? (
                                                                <div className="flex flex-col gap-1.5">
                                                                    <span className="text-[11px] font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded border border-orange-200 w-fit">
                                                                        Content Held ({item.scrapedContent?.length} chars)
                                                                    </span>
                                                                    <button
                                                                        onClick={() => {
                                                                            openHoldModal(item);
                                                                        }}
                                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold
                                                                                   bg-orange-500 text-white hover:bg-orange-600
                                                                                   transition-all shadow-sm"
                                                                    >
                                                                        <AlertTriangle className="w-3.5 h-3.5" />
                                                                        Edit & Approve
                                                                    </button>
                                                                </div>
                                                            ) : item.chatgptOutput ? (
                                                                <div className="flex items-center gap-1.5">
                                                                    <button
                                                                        onClick={() => {
                                                                            // Find the index of this item in filteredItems
                                                                            const idx = filteredItems.findIndex(fi => fi.id === item.id);
                                                                            setOutputModalIndex(idx >= 0 ? idx : 0);
                                                                        }}
                                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
                                                                                   bg-primary-50 text-primary-700 hover:bg-primary-100
                                                                                   transition-all border border-primary-200"
                                                                    >
                                                                        <Eye className="w-3.5 h-3.5" />
                                                                        View Data
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400 text-xs italic">No output</span>
                                                            )}
                                                        </td>

                                                        {/* Source */}
                                                        <td className="px-3 py-3">
                                                            <SourceBadge source={item.sourceType} />
                                                        </td>

                                                        {/* Status (editable) */}
                                                        <td className="px-3 py-3">
                                                            {isSaving ? (
                                                                <span className="inline-flex items-center gap-1.5 text-xs text-sky-600 font-medium">
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                    Saving…
                                                                </span>
                                                            ) : (
                                                                <div className="relative inline-block">
                                                                    <select
                                                                        value={currentStatus}
                                                                        onChange={(e) => handleStatusChange(item, e.target.value)}
                                                                        className={`appearance-none pl-2.5 pr-7 py-1.5 rounded-full text-xs font-semibold ring-1 ring-inset
                                                                            cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-300
                                                                            transition-all duration-200
                                                                            ${STATUS_BADGE_COLORS[REVIEW_STATUS[currentStatus]?.color] || STATUS_BADGE_COLORS.amber}`}
                                                                    >
                                                                        {Object.entries(REVIEW_STATUS).map(([key, cfg]) => (
                                                                            <option key={key} value={key}>{cfg.label}</option>
                                                                        ))}
                                                                    </select>
                                                                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-50" />
                                                                </div>
                                                            )}
                                                        </td>

                                                        {/* Log button — only for authorized users */}
                                                        {canViewLogs && (
                                                        <td className="px-3 py-3">
                                                            <button
                                                                onClick={() => setLogSidebarItem(item)}
                                                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium
                                                                           transition-all border ${item.processingLog && item.processingLog.length > 0
                                                                    ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200'
                                                                    : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border-slate-200'}`}
                                                                title="View processing details"
                                                            >
                                                                <ScrollText className="w-3.5 h-3.5" />
                                                            </button>
                                                        </td>
                                                        )}
                                                    </tr>

                                                    {/* Expanded JSON output row */}
                                                    {isExpanded && item.chatgptOutput && (
                                                        <tr key={`${item.id}-detail`} className="bg-slate-50/60">
                                                            <td colSpan={columns.length} className="px-4 py-4">
                                                                <div className="max-w-4xl">
                                                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                                                                        AI Output — {item.partNumber || `Row #${item.rowNumber || item.id}`}
                                                                    </p>
                                                                    <pre className="bg-white rounded-xl p-4 border border-slate-200 overflow-auto max-h-64
                                                                                    text-[11px] text-slate-700 leading-relaxed font-mono shadow-inner">
                                                                        {typeof item.chatgptOutput === 'string'
                                                                            ? item.chatgptOutput
                                                                            : String(item.chatgptOutput || '')
                                                                        }
                                                                    </pre>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* ── Pagination ──────────────────────────── */}
                            <div className="shrink-0 flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-slate-50/60 border-t border-slate-100">
                                <p className="text-xs text-slate-500">
                                    Showing <span className="font-semibold text-slate-700">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>
                                    {' '}to{' '}
                                    <span className="font-semibold text-slate-700">{Math.min(currentPage * ITEMS_PER_PAGE, filteredItems.length)}</span>
                                    {' '}of{' '}
                                    <span className="font-semibold text-slate-700">{filteredItems.length}</span> results
                                </p>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-200/60 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>

                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let page;
                                        if (totalPages <= 5) {
                                            page = i + 1;
                                        } else if (currentPage <= 3) {
                                            page = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            page = totalPages - 4 + i;
                                        } else {
                                            page = currentPage - 2 + i;
                                        }
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all
                                                    ${currentPage === page
                                                        ? 'bg-primary-600 text-white shadow-sm'
                                                        : 'text-slate-600 hover:bg-slate-200/60'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-200/60 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ── Confirmation Popup Modal ────────────────── */}
            {confirmItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-fade-in">
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md mx-4 p-6 animate-slide-up">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">Save to Database?</h3>
                                <p className="text-sm text-slate-500">Status is Completed. Save this record to DB?</p>
                            </div>
                        </div>

                        {/* Record preview */}
                        <div className="bg-slate-50 rounded-xl p-4 mb-5 border border-slate-100 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500 font-medium">MFR Part # 1</span>
                                <span className="font-semibold text-slate-800 text-right max-w-[200px] truncate">
                                    {getMfrPN1(confirmItem) || '—'}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500 font-medium">Supplier Part #</span>
                                <span className="text-slate-700 text-right max-w-[200px] truncate">
                                    {confirmItem.givenSupplierPartnumber || '—'}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500 font-medium">Description 1 / Description 2</span>
                                <span className="text-slate-700 text-right max-w-[200px] truncate">
                                    {combineDescriptions(confirmItem, ' / ') || '—'}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500 font-medium">Noun/Modifier</span>
                                <span className="text-slate-700 text-right max-w-[200px] truncate">
                                    {confirmItem.nounModifier || '—'}
                                </span>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => setConfirmItem(null)}
                                className="flex-1 btn-secondary text-sm !py-2.5 font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmSaveToDB}
                                className="flex-1 btn-primary text-sm !py-2.5 font-semibold"
                            >
                                <Database className="w-4 h-4" />
                                Save to DB
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* ── Duplicate Comparison Modal ───────────────── */}
            {duplicateModal && (
                <div className="fixed inset-0 z-[55] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-fade-in">
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col animate-slide-up">
                        {/* Header */}
                        <div className="flex items-center gap-3 p-5 border-b border-slate-100 shrink-0">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-slate-900">Duplicate Part Number Found</h3>
                                <p className="text-sm text-slate-500">
                                    Matched on <span className="font-semibold text-amber-700">{duplicateModal.matchedColumn}</span> — compare data below
                                </p>
                            </div>
                            <button onClick={() => setDuplicateModal(null)} className="text-slate-400 hover:text-slate-600 p-1">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Comparison Table */}
                        <div className="flex-1 overflow-auto p-5">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-slate-50">
                                        <th className="text-left p-2.5 font-semibold text-slate-600 border-b border-slate-200 w-[30%]">Field</th>
                                        <th className="text-left p-2.5 font-semibold text-blue-700 border-b border-slate-200 w-[35%]">
                                            <Database className="w-3.5 h-3.5 inline mr-1.5" />Existing (DB)
                                        </th>
                                        <th className="text-left p-2.5 font-semibold text-emerald-700 border-b border-slate-200 w-[35%]">
                                            <RefreshCw className="w-3.5 h-3.5 inline mr-1.5" />New (AI)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const allKeys = [...new Set([
                                            ...Object.keys(duplicateModal.existingData || {}),
                                            ...Object.keys(duplicateModal.newData || {})
                                        ])];
                                        return allKeys.map(key => {
                                            const oldVal = duplicateModal.existingData?.[key] ?? '';
                                            const newVal = duplicateModal.newData?.[key] ?? '';
                                            const isDiff = String(oldVal).trim() !== String(newVal).trim();
                                            return (
                                                <tr key={key} className={isDiff ? 'bg-amber-50/60' : ''}>
                                                    <td className="p-2.5 font-medium text-slate-700 border-b border-slate-100 break-all">{key}</td>
                                                    <td className={`p-2.5 border-b border-slate-100 break-all ${isDiff ? 'text-blue-800 font-medium' : 'text-slate-500'}`}>
                                                        {oldVal || <span className="text-slate-300 italic">null</span>}
                                                    </td>
                                                    <td className={`p-2.5 border-b border-slate-100 break-all ${isDiff ? 'text-emerald-800 font-medium' : 'text-slate-500'}`}>
                                                        {newVal || <span className="text-slate-300 italic">null</span>}
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3 p-5 border-t border-slate-100 shrink-0">
                            <button
                                onClick={() => setDuplicateModal(null)}
                                className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <div className="flex-1" />
                            <button
                                onClick={keepExistingRecord}
                                className="px-5 py-2.5 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors border border-blue-200"
                            >
                                <Database className="w-4 h-4 inline mr-1.5" />
                                Keep Existing
                            </button>
                            <button
                                onClick={forceOverwriteSave}
                                className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors shadow-sm"
                            >
                                <RefreshCw className="w-4 h-4 inline mr-1.5" />
                                Overwrite with New
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Delete Confirmation Popup ───────────────── */}
            {deletePopupOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                        {/* Header */}
                        <div className="p-5 border-b border-slate-100 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">Delete {selectedIds.size} Item{selectedIds.size > 1 ? 's' : ''}</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Choose what to delete</p>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-5 space-y-4">
                            <p className="text-sm text-slate-600">
                                Select where you want to delete the selected items from:
                            </p>

                            {/* Delete from Temp checkbox */}
                            <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-primary-300 cursor-pointer transition-all">
                                <input
                                    type="checkbox"
                                    checked={deleteFromTemp}
                                    onChange={(e) => setDeleteFromTemp(e.target.checked)}
                                    className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                                />
                                <div className="flex-1">
                                    <span className="text-sm font-semibold text-slate-700">Delete from Temp (Browser)</span>
                                    <p className="text-xs text-slate-500 mt-0.5">Removes from local browser storage (IndexedDB)</p>
                                </div>
                            </label>

                            {/* Delete from DB checkbox */}
                            <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-red-300 cursor-pointer transition-all">
                                <input
                                    type="checkbox"
                                    checked={deleteFromDB}
                                    onChange={(e) => setDeleteFromDB(e.target.checked)}
                                    className="w-4 h-4 rounded text-red-600 focus:ring-red-500"
                                />
                                <div className="flex-1">
                                    <span className="text-sm font-semibold text-slate-700">Delete from DB (Major_table)</span>
                                    <p className="text-xs text-slate-500 mt-0.5">Permanently removes from database server</p>
                                </div>
                            </label>

                            {deleteFromDB && (
                                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>Database deletions are permanent and cannot be undone.</span>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setDeletePopupOpen(false)}
                                disabled={deleting}
                                className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleting || (!deleteFromTemp && !deleteFromDB)}
                                className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700
                                           disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Confirm Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Batch Status Modal ──────────────────────── */}
            <BatchStatusModal
                isOpen={batchModalOpen}
                onClose={() => setBatchModalOpen(false)}
                batchJobName={batchJobName}
                onCheckStatus={handleCheckBatchStatus}
                onFetchResults={handleFetchBatchResults}
                onRetry={handleRetryBatch}
                statusData={batchStatusData}
                isLoading={batchStatusLoading}
                isFetchingResults={batchResultLoading}
            />

            {/* ── Processing Log Sidebar ──────────────────── */}
            {canViewLogs && <ProcessingLogModal item={logSidebarItem} onClose={closeLogModal} />}

            {/* ── HOLD PROMPT EDITOR MODAL ────────────────────────────────────── */}
            {holdModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50 rounded-t-xl">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                                    Review Scraped Content (Step 3)
                                </h3>
                                <p className="text-sm text-slate-500 mt-0.5">Review and edit the scraped content before processing (Steps 4→5→6)</p>
                            </div>
                            <button
                                onClick={() => setHoldModalOpen(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6 flex-1 overflow-y-auto">
                            {holdLoading ? (
                                <div className="flex flex-col items-center justify-center h-48 gap-3">
                                    <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                                    <p className="text-sm font-medium text-slate-500 animate-pulse">Loading scraped content...</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Stats bar */}
                                    <div className="flex items-center gap-2 flex-wrap text-xs">
                                        <span className="px-2 py-1 rounded bg-slate-100 text-slate-600">
                                            Total: {holdPrompt.length.toLocaleString()} chars
                                        </span>
                                        <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">
                                            Alphanumeric: {(holdPrompt.match(/[A-Za-z0-9]/g) || []).length.toLocaleString()} chars
                                        </span>
                                        <span className="px-2 py-1 rounded bg-violet-50 text-violet-700">
                                            ~{Math.ceil(holdPrompt.length / 3).toLocaleString()} tokens (chars÷3)
                                        </span>
                                    </div>
                                    <div className="bg-slate-800 rounded-lg p-4">
                                        <p className="text-xs text-amber-400 font-semibold mb-2 flex items-center justify-between">
                                            <span>📄 Scraped Content (editable — edit before processing)</span>
                                            <span className="text-slate-400 font-normal">Edit below before sending</span>
                                        </p>
                                        <textarea
                                            value={holdPrompt}
                                            onChange={(e) => setHoldPrompt(e.target.value)}
                                            className="w-full h-80 text-sm text-emerald-200 bg-slate-900 border border-slate-700/50 rounded-lg p-4 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono leading-relaxed resize-y"
                                            style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500 italic">
                                        This is the raw scraped content from Step 3. Edit if needed, then click Approve to run Steps 4→5→6 (Spec Extract → Prompt Build → AI Extract).
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-xl flex items-center justify-end gap-3">
                            <button
                                onClick={() => setHoldModalOpen(false)}
                                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
                                disabled={holdSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitHoldPrompt}
                                disabled={holdLoading || holdSubmitting || !holdPrompt.trim()}
                                className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {holdSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                Approve & Process (Steps 4→5→6)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Output Data Modal ─────────────────────── */}
            {outputModalIndex !== null && (
                <OutputDataModal
                    items={filteredItems}
                    currentIndex={outputModalIndex}
                    onClose={() => setOutputModalIndex(null)}
                    onNavigate={(idx) => setOutputModalIndex(idx)}
                />
            )}

            {/* ── Scrap Cart (floating) ─────────────────── */}
            <ScrapCart
                cartItems={cartItems}
                allItems={items}
                onRemove={(id) => setCartItems(prev => prev.filter(cid => cid !== id))}
                onClearCart={() => setCartItems([])}
                onApprove={async (ids) => {
                    for (const id of ids) {
                        const item = items.find(i => i.id === id);
                        if (item) await handleStatusChange(item, 'COMPLETED');
                    }
                    setCartItems(prev => prev.filter(cid => !ids.includes(cid)));
                }}
                onHold={async (ids) => {
                    for (const id of ids) {
                        const item = items.find(i => i.id === id);
                        if (item) await handleStatusChange(item, 'HOLD');
                    }
                    setCartItems(prev => prev.filter(cid => !ids.includes(cid)));
                }}
                onReScrap={(ids) => {
                    const reItems = ids.map(id => items.find(i => i.id === id)).filter(Boolean);
                    const rescrapeKey = `rescrape-cart-${Date.now()}`;
                    sessionStorage.setItem(rescrapeKey, JSON.stringify(reItems));
                    navigate('/', { state: { rescrapeKey, rescrapeCount: reItems.length, autoStart: true } });
                }}
            />
        </div>
    );
}

export default ResultsPage;
